import React, { useState } from 'react';
import { ChevronRight, ChevronDown, ExternalLink } from 'lucide-react';

interface PageInfo {
  url: string;
  title?: string;
  [key: string]: any;
}

interface InlineCategoryCardProps {
  categoryName: string;
  icon?: string;
  pages: PageInfo[];
  count: number;
  listingCount?: number;
  listingChildrenCount?: number;
  sampleProducts?: string[];
  defaultExpanded?: boolean;
}

const extractTitleFromUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    const path = urlObj.pathname;
    const segments = path.split('/').filter(Boolean);
    const lastSegment = segments[segments.length - 1] || urlObj.hostname;
    return lastSegment
      .replace(/[-_]/g, ' ')
      .replace(/\.(html|php|aspx?)$/i, '')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  } catch {
    return url;
  }
};

export const InlineCategoryCard: React.FC<InlineCategoryCardProps> = ({
  categoryName,
  icon,
  pages,
  count,
  listingCount,
  listingChildrenCount,
  sampleProducts,
  defaultExpanded = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const productCount = typeof count === 'number' ? count : 0;

  return (
    <div
      className={`
        ${isExpanded
          ? 'flex flex-col w-full max-w-3xl flex-auto'
          : 'flex flex-col flex-none'
        }
        transition-all duration-300 ease-out
      `}
      style={
        isExpanded
          ? { flex: '1 1 320px' }
          : { flex: '0 0 auto', width: 'fit-content' }
      }
    >
      <div
        className={`
          bg-white rounded-xl border-2 overflow-hidden
          transition-all duration-200
          ${isExpanded
            ? 'w-full border-blue-300 shadow-lg'
            : 'inline-flex flex-col w-auto border-gray-200 hover:border-blue-200 hover:shadow-md hover:scale-105 cursor-pointer'
          }
        `}
      >
        {/* Header - Always Visible */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={`
            px-4 py-3 flex items-center gap-3 cursor-pointer
            transition-colors duration-200
            ${isExpanded ? 'w-full bg-blue-50' : 'bg-white hover:bg-gray-50'}
          `}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="transition-transform duration-300 flex-shrink-0">
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 text-blue-600" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-500" />
              )}
            </div>
            {icon && <span className="text-xl flex-shrink-0">{icon}</span>}
            <span className={`font-semibold text-sm whitespace-nowrap flex-shrink-0 ${isExpanded ? 'text-blue-900' : 'text-gray-800'}`}>
              {categoryName}
            </span>
          </div>
          <span
            className={`
              ml-auto inline-flex items-center justify-center
              text-xs font-semibold
              px-2.5 py-1 rounded-full
              ${isExpanded ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-700'}
            `}
          >
            {productCount}
          </span>
        </button>

        {/* Expandable Content */}
        <div
          className={`
            transition-all duration-300 ease-out overflow-hidden
            ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}
          `}
        >
          <div className="border-t border-gray-200">
            {pages.length > 0 ? (
              <div className="p-3 max-h-80 overflow-y-auto custom-scrollbar">
                <div className="space-y-2">
                  {pages.map((page, index) => {
                    const displayTitle = page.title || extractTitleFromUrl(page.url);
                    return (
                      <a
                        key={`${page.url}-${index}`}
                        href={page.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group block rounded-lg border border-gray-200 p-2.5 hover:border-blue-400 hover:bg-blue-50 transition-all duration-150"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-gray-800 group-hover:text-blue-600 truncate transition-colors">
                              {displayTitle}
                            </div>
                            <div className="text-xs text-gray-500 truncate mt-0.5">
                              {page.url}
                            </div>
                          </div>
                          <ExternalLink className="w-3.5 h-3.5 text-gray-400 group-hover:text-blue-500 flex-shrink-0 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </a>
                    );
                  })}
                </div>
              </div>
            ) : Array.isArray(sampleProducts) && sampleProducts.length > 0 ? (
              <div className="p-3 space-y-2">
                {sampleProducts.slice(0, 5).map((url, index) => {
                  const displayTitle = extractTitleFromUrl(url);
                  return (
                    <a
                      key={`${url}-${index}`}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group block rounded-lg border border-gray-200 p-2.5 hover:border-blue-400 hover:bg-blue-50 transition-all duration-150"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-800 group-hover:text-blue-600 truncate transition-colors">
                            {displayTitle}
                          </div>
                          <div className="text-xs text-gray-500 truncate mt-0.5">
                            {url}
                          </div>
                        </div>
                        <ExternalLink className="w-3.5 h-3.5 text-gray-400 group-hover:text-blue-500 flex-shrink-0 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </a>
                  );
                })}
              </div>
            ) : (
              <div className="p-6 text-center">
                <div className="text-2xl mb-2">📄</div>
                <p className="text-sm text-gray-500">No pages in this category</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InlineCategoryCard;
